import type { FormGroup } from '@angular/forms';
import type { ShipmentAddressSectionKey } from '../models/address-form.model';
import { ADDRESS_VALIDATION_ERROR_KEYS } from '../validators/address-field.validators';
import { detectPostalKeywordInAddress } from './detect-postal-keyword-address';

/** Sections whose phone number becomes required for postal keyword addresses (Ship From is always required). */
const PHONE_REQUIRED_FOR_POSTAL_KEYWORD_SECTIONS: readonly ShipmentAddressSectionKey[] = [
  'shipTo',
  'returnAddress',
];

/**
 * Applies supplemental validators when PO Box, Rural Route, or General Delivery is detected.
 * Requires civic address in Address Line 2 and a phone number on Ship To and, when enabled,
 * the Return Address (OM-693).
 */
export function applyRuralRoutePoBoxValidation(form: FormGroup, submitted: boolean): void {
  if (!submitted) {
    return;
  }

  const sectionKeys: ShipmentAddressSectionKey[] = ['shipFrom', 'shipTo'];
  if (form.get('useDifferentReturnAddress')?.value === true) {
    sectionKeys.push('returnAddress');
  }

  for (const sectionKey of sectionKeys) {
    const sectionGroup = form.get(sectionKey) as FormGroup | null;
    if (!sectionGroup) {
      continue;
    }

    const streetAddress = String(sectionGroup.get('streetAddress')?.value ?? '');
    const addressLine3 = String(sectionGroup.get('addressLine3')?.value ?? '');

    if (!detectPostalKeywordInAddress(streetAddress, addressLine3)) {
      clearRuralRouteErrors(sectionGroup);
      continue;
    }

    const addressLine2Control = sectionGroup.get('addressLine2');
    const phoneControl = sectionGroup.get('phoneNumber');

    if (addressLine2Control && !String(addressLine2Control.value ?? '').trim()) {
      addressLine2Control.setErrors({
        ...addressLine2Control.errors,
        [ADDRESS_VALIDATION_ERROR_KEYS.civicAddressRequired]: true,
      });
      addressLine2Control.markAsTouched();
    }

    if (
      PHONE_REQUIRED_FOR_POSTAL_KEYWORD_SECTIONS.includes(sectionKey) &&
      phoneControl &&
      !String(phoneControl.value ?? '').trim()
    ) {
      phoneControl.setErrors({
        ...phoneControl.errors,
        [ADDRESS_VALIDATION_ERROR_KEYS.phoneRequiredPostalKeyword]: true,
      });
      phoneControl.markAsTouched();
    }
  }

  form.updateValueAndValidity({ emitEvent: false });
}

/**
 * Clears rural-route supplemental errors from a section when keywords are no longer present.
 */
function clearRuralRouteErrors(sectionGroup: FormGroup): void {
  for (const field of ['addressLine2', 'phoneNumber'] as const) {
    const control = sectionGroup.get(field);
    if (!control?.errors) {
      continue;
    }

    const remainingErrors = { ...control.errors };
    delete remainingErrors[ADDRESS_VALIDATION_ERROR_KEYS.civicAddressRequired];
    delete remainingErrors[ADDRESS_VALIDATION_ERROR_KEYS.phoneRequiredPostalKeyword];
    control.setErrors(Object.keys(remainingErrors).length > 0 ? remainingErrors : null);
  }
}
